// components/InternVendorDialog.tsx
import AsyncMultiSelectField from "@/components/form/components/AsyncMultiSelectField";
import InputField from "@/components/form/components/InputField";
import SelectField from "@/components/form/components/SelectField";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useCreateInternVendorMutation,
  useUpdateInternVendorMutation,
} from "@/hooks/api/internVendor.queries";
import { departmentService } from "@/services/department.service";
import {
  InternVendorCreateType,
  InternVendorParamsType,
  InternVendorType,
  internVendorService,
} from "@/services/internVendor.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import TextareaField from "../form/components/Textarea";

type InternVendorDialogProps = {
  mode: "create" | "edit";
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: InternVendorParamsType;
  vendorId?: string;
  vendorData?: InternVendorType | null;
  onCancel: () => void;
};

const internVendorSchema = z.object({
  companyName: z.string().min(1, "Company Name is required"),
  contactPerson: z.string().min(1, "Contact Person is required"),
  email: z.email("Invalid email").min(1, "Email is required"),
  phone: z.string().min(1, "Phone is required"),
  website: z.string().optional(),
  status: z.enum(["active", "inactive", "pending"]),
  industry: z.array(z.string()).min(1, "Select at least one industry"),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  preferredDepartments: z.array(z.string()).optional(),
  duration: z.number().min(0).optional(),
  stipendRange: z.string().optional(),
  availablePositions: z.number().min(0).optional(),
  companyDescription: z.string().optional(),
  internshipRequirements: z.string().optional(),
});

type InternVendorFormType = z.infer<typeof internVendorSchema>;

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "Pending", value: "pending" },
];

const InternVendorDialog = ({
  mode,
  isOpen,
  onOpenChange,
  filters,
  vendorId,
  vendorData,
  onCancel,
}: InternVendorDialogProps) => {
  const createMutation = useCreateInternVendorMutation(filters);
  const updateMutation = useUpdateInternVendorMutation(filters);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const form = useForm<InternVendorFormType>({
    resolver: zodResolver(internVendorSchema),
    defaultValues: {
      companyName: "",
      contactPerson: "",
      email: "",
      phone: "",
      website: "",
      status: "active",
      industry: [],
      city: "",
      state: "",
      preferredDepartments: [],
      duration: 0,
      stipendRange: "",
      availablePositions: 0,
      companyDescription: "",
      internshipRequirements: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && vendorData) {
        setTimeout(() => {
          form.reset({
            companyName: vendorData.companyName,
            contactPerson: vendorData.contactPerson,
            email: vendorData.email,
            phone: vendorData.phone,
            website: vendorData.website || "",
            status: vendorData.status,
            industry: vendorData.industry || [],
            city: vendorData.city || "",
            state: vendorData.state || "",
            preferredDepartments: vendorData.preferredDepartments || [],
            duration: vendorData.duration || 0,
            stipendRange: vendorData.stipendRange || "",
            availablePositions: vendorData.availablePositions || 0,
            companyDescription: vendorData.companyDescription || "",
            internshipRequirements: vendorData.internshipRequirements || "",
          });
        }, 0);
      } else {
        form.reset({
          companyName: "",
          contactPerson: "",
          email: "",
          phone: "",
          website: "",
          status: "active",
          industry: [],
          city: "",
          state: "",
          preferredDepartments: [],
          duration: 0,
          stipendRange: "",
          availablePositions: 0,
          companyDescription: "",
          internshipRequirements: "",
        });
      }
    }
  }, [mode, vendorData, isOpen, form]);

  const handleSubmit = (values: InternVendorFormType) => {
    const submissionData: InternVendorCreateType = {
      ...values,
      website: values.website || "",
      industry: values.industry || [],
      city: values.city || "",
      state: values.state || "",
      preferredDepartments: values.preferredDepartments || [],
      duration: values.duration || 0,
      stipendRange: values.stipendRange || "",
      availablePositions: values.availablePositions || 0,
      companyDescription: values.companyDescription || "",
      internshipRequirements: values.internshipRequirements || "",
    };

    if (mode === "create") {
      createMutation.mutate(submissionData, { onSuccess: onCancel });
    } else if (mode === "edit" && vendorId) {
      updateMutation.mutate(
        { _id: vendorId, data: submissionData },
        { onSuccess: onCancel }
      );
    }
  };

  // Convert array of strings to array of objects for default options
  const defaultIndustryOptions =
    vendorData?.industry?.map((i) => ({
      label: i,
      value: i,
    })) || [];

  // TODO: Since we don't have full Department objects in vendorData.preferredDepartments (only IDs),
  // we might need to fetch them or accept that they won't show labels correctly if not loaded.
  // For now, let's assume valid IDs are passed and let AsyncMultiSelectField handle fetching by ID if implemented,
  // OR just provide value as label as fallback if we can't look up labels synchronously.
  // Ideally, `defaultOptions` should be rich objects.
  // If `preferredDepartments` are just IDs, the AsyncMultiSelectField might show IDs unless we pre-fetch details.
  // Given previous pattern (StaffDialog subjects), we might just pass empty default or try to construct it.
  // For this refactor, I will pass empty defaultOptions for Depts if we only have IDs,
  // relying on the user to see "Loading..." or empty if not implemented to fetch by ID.
  // Actually, AsyncMultiSelectField in this project often takes `defaultOptions` which are {label, value}[].
  // If we only have IDs, we can't show correct labels immediately without fetching.
  // Let's pass empty array for now as typical "edit" might require fetching those specific IDs to get labels.
  // However, `AsyncMultiSelectField` Logic: it uses `defaultOptions` to set initial selected state.
  // If we don't provide them, the field will appear empty.
  // I will map IDs to IDs for label simply to show *something* or check if I can fetch.
  // For simplicity and robustness (as I can't easily fetch all depts here synchronously),
  // I will rely on the fact that if it's not crucial to show labels immediately, or if the component handles it.
  // Wait, `StaffDialog` has `subjects` which is string array, but that was just a text Input in old code?
  // In `AdminDialog`, we fixed `AsyncMultiSelectField` to handle `defaultOptions`.
  // I will try to map ID->ID for now, but in real app this needs `fetchDepartmentsByIds`.
  const defaultDepartmentOptions =
    vendorData?.preferredDepartments?.map((id) => ({
      label: `Dept ${id}`, // Placeholder label as we don't have names easily
      value: id,
    })) || [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? "Add New Internship Vendor"
              : "Edit Internship Vendor"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2"
          >
            {/* Company Name */}
            <InputField<InternVendorFormType>
              type="text"
              name="companyName"
              label="Company Name"
              placeholder="e.g. TechCorp Solutions"
              required
              disabled={isPending}
            />

            {/* Contact Person */}
            <InputField<InternVendorFormType>
              type="text"
              name="contactPerson"
              label="Contact Person"
              placeholder="e.g. John Parker"
              required
              disabled={isPending}
            />

            {/* Email */}
            <InputField<InternVendorFormType>
              name="email"
              label="Email"
              placeholder="e.g. john@techcorp.com"
              type="email"
              required
              disabled={isPending}
            />

            {/* Phone */}
            <InputField<InternVendorFormType>
              type="tel"
              name="phone"
              label="Phone Number"
              placeholder="e.g. 9876543210"
              inputMode="numeric"
              maxLength={10}
              required
              disabled={isPending}
            />

            {/* Website */}
            <InputField<InternVendorFormType>
              type="url"
              name="website"
              label="Website"
              placeholder="https://example.com"
              disabled={isPending}
            />

            {/* Status */}
            <SelectField<InternVendorFormType>
              name="status"
              label="Status"
              options={STATUS_OPTIONS}
              required
              disabled={isPending}
            />

            {/* Industry - Multi Select */}
            <div className="md:col-span-2">
              <AsyncMultiSelectField<InternVendorFormType>
                name="industry"
                label="Industry"
                placeholder="Select industries"
                fetchOptions={internVendorService.fetchIndustryOptions}
                queryKey={["industries"]}
                defaultOptions={defaultIndustryOptions}
                required
                disabled={isPending}
              />
            </div>

            {/* City */}
            <InputField<InternVendorFormType>
              type="text"
              name="city"
              label="City"
              placeholder="e.g. Bangalore"
              required
              disabled={isPending}
            />

            {/* State */}
            <InputField<InternVendorFormType>
              type="text"
              name="state"
              label="State"
              placeholder="e.g. Karnataka"
              required
              disabled={isPending}
            />

            {/* Preferred Departments - Multi Select */}
            <div className="md:col-span-2">
              <AsyncMultiSelectField<InternVendorFormType>
                name="preferredDepartments"
                label="Preferred Departments"
                placeholder="Select preferred departments"
                fetchOptions={departmentService.fetchDepartmentOptions}
                queryKey={["departments"]}
                defaultOptions={defaultDepartmentOptions}
                disabled={isPending}
              />
            </div>

            {/* Duration */}
            <InputField<InternVendorFormType>
              name="duration"
              label="Duration (months)"
              placeholder="e.g. 3"
              type="number"
              disabled={isPending}
            />

            {/* Stipend Range */}
            <InputField<InternVendorFormType>
              type="number"
              name="stipendRange"
              label="Stipend Range"
              placeholder="e.g. 10000-15000"
              disabled={isPending}
            />

            {/* Available Positions */}
            <div className="md:col-span-2">
              <InputField<InternVendorFormType>
                name="availablePositions"
                label="Available Positions"
                placeholder="e.g. 5"
                type="number"
                disabled={isPending}
              />
            </div>

            {/* Company Description */}
            <div className="md:col-span-2">
              <TextareaField<InternVendorFormType>
                name="companyDescription"
                label="Company Description"
                placeholder="Brief description about the company"
                disabled={isPending}
              />
            </div>

            {/* Internship Requirements */}
            <div className="md:col-span-2">
              <TextareaField<InternVendorFormType>
                name="internshipRequirements"
                label="Internship Requirements"
                placeholder="Skills, qualifications, or other requirements"
                disabled={isPending}
              />
            </div>

            <DialogFooter className="md:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? mode === "create"
                    ? "Adding..."
                    : "Saving..."
                  : mode === "create"
                    ? "Create Vendor"
                    : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default InternVendorDialog;
